"use client";
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useToast } from '../components/Toast';

const API = '/api';

export default function WaitingListPage(){
  const [loading, setLoading] = useState(false);
  const { showToast, ToastComponent } = useToast();

  async function submit(e: React.FormEvent<HTMLFormElement>){
    e.preventDefault(); setLoading(true);
    const data = new FormData(e.currentTarget);
    const obj = Object.fromEntries(data.entries());
    try {
      const res = await fetch(`${API}/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(obj)
      });
      const j = await res.json();
      if(res.ok) {
        showToast('Added to waiting list. Thank you!', 'success');
        e.currentTarget.reset();
      } else {
        showToast('Error: ' + (j.error || 'unknown'), 'error');
      }
    } catch (err) {
      showToast('Network error. Please try again.', 'error');
    } finally { setLoading(false); }
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-warm-brown mb-4">Reserve Your LINDY Companion</h1>
          <p className="text-xl text-warm-brown/70 max-w-3xl mx-auto">
            Join the waitlist for first picks on upcoming litters, quick temperament consults, and early invites to meet your kitten.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {[
            { title: 'Concierge matching', detail: 'Fast calls that pair kitten energy with your home rhythm.' },
            { title: 'Maison previews', detail: 'Private video walk-throughs and salon invites before public release.' },
            { title: 'Ongoing guidance', detail: 'Nutrition, grooming, and enrichment notes from our care team for life.' }
          ].map((item) => (
            <div key={item.title} className="bg-white/80 backdrop-blur rounded-3xl border border-sand/20 p-6 shadow-sm">
              <h3 className="font-heading text-lg text-warm-brown mb-2">{item.title}</h3>
              <p className="text-sm text-warm-brown/70 leading-relaxed">{item.detail}</p>
            </div>
          ))}
        </motion.div>

        <motion.div 
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="bg-white/90 backdrop-blur rounded-3xl shadow-2xl p-8 md:p-12">
            <form onSubmit={submit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Full Name *</label>
                  <input 
                    name="name" 
                    placeholder="Enter your full name" 
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200" 
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Email Address *</label>
                  <input 
                    name="email" 
                    type="email" 
                    placeholder="your.email@example.com" 
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200" 
                    required 
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-brown mb-2">Phone Number</label>
                <input 
                  name="phone" 
                  type="tel" 
                  placeholder="+1 (606) 465-2993" 
                  className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200" 
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Preferred Contact Method</label>
                  <select 
                    name="contactPreference" 
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Timeline Preference</label>
                  <select 
                    name="timeline" 
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200"
                  >
                    <option value="asap">As soon as possible</option>
                    <option value="3months">Within 3 months</option>
                    <option value="6months">Within 6 months</option>
                    <option value="flexible">I'm flexible</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-brown mb-2">Tell us about your ideal companion</label>
                <textarea 
                  name="preferences" 
                  placeholder="Preferred color points, personality nuances, household rhythm, existing pets..." 
                  rows={4}
                  className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-brown mb-2">Additional Questions or Notes</label>
                <textarea 
                  name="message" 
                  placeholder="Share travel preferences, care inquiries, or anything you'd like us to know." 
                  rows={3}
                  className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all duration-200" 
                />
              </div>

              <motion.button 
                disabled={loading} 
                type="submit" 
                className="w-full py-4 bg-soft-gold text-warm-brown rounded-xl hover:bg-soft-gold/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-lg shadow-lg hover:shadow-xl"
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-warm-brown mr-2"></div>
                    Reserving your place...
                  </div>
                ) : (
                  'Join the LINDY Waiting List'
                )}
              </motion.button>

              <p className="text-sm text-warm-brown/60 text-center mt-4">
                We cherish discretion. Expect thoughtful updates aligned with your timeline and preferences — never spam, always LINDY care.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
      {ToastComponent}
    </main>
  );
}
